import { Image } from 'expo-image';
import { useMemo } from 'react';
import React from 'react';
import { useIntl } from 'react-intl';
import { View } from 'react-native';

import { DoublePointsMondayAlertBanner, RewardsPointsProgress } from '@fhs/loyalty/src/components';
import { Button, IconMenu, MqSwitch, Text, createMqStyles, tokens, useMqSelect } from '@fhs/ui';
import { useAuthContext } from '@fhs-legacy/frontend/src/state/auth';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import { InlineAlert } from '@fhs-legacy/universal-components';

import { useLoyaltyLegacyStates } from '../../hooks/use-loyalty-legacy-states';
import { useMaxRedemptionLimitAlert } from '../../hooks/use-max-redemption-limit-alert';
import { TierProgressAlert } from '../../hooks/use-tier-progress-alert';

import { useIsLoyaltyPageLoading } from './hooks';
import { InStoreCard } from './in-store-card';

const DAYS_TO_SHOW_POINT_EXPIRE_DISCLAIMER = 30;

interface Props {
  onPressSeeHistory: () => void;
}

export function RewardsSectionHeader({ onPressSeeHistory }: Props) {
  const { isAuthenticated } = useAuthContext();
  const mqStyles = useMqStyles();
  const isLoyaltyPageLoading = useIsLoyaltyPageLoading();

  const { formatMessage } = useIntl();
  const { loyaltyUser, loyaltyUserExpireDateFormattedText } = useLoyaltyLegacyStates();
  const hidePointsExpirationEnabled = useFlag('enable-hide-points-expiration');
  const renderMaxRedemptionLimitAlert = useMaxRedemptionLimitAlert();

  const shouldShowExpiryDisclaimer = useMemo(() => {
    if (hidePointsExpirationEnabled || !loyaltyUser?.pointsExpiryDateKey) {
      return false;
    }
    const now = new Date();
    const loyaltyPointExpireDate = new Date(loyaltyUser?.pointsExpiryDateKey);

    const diffInDays = Math.round(
      (loyaltyPointExpireDate.getTime() - now.getTime()) / (1000 * 3600 * 24)
    );

    return diffInDays <= DAYS_TO_SHOW_POINT_EXPIRE_DISCLAIMER;
  }, [loyaltyUser?.pointsExpiryDateKey, hidePointsExpirationEnabled]);

  return (
    <View style={mqStyles.container}>
      <View style={mqStyles.content}>
        <Text.Heading type={useMqSelect({ $gteDesktop: 'one' }, 'three')}>My Rewards</Text.Heading>
        {isAuthenticated && (
          <Button type="outline-subtle" size="sm" onPress={onPressSeeHistory}>
            <Button.Icon>
              <IconMenu />
            </Button.Icon>
            <Button.Text>View History</Button.Text>
          </Button>
        )}
      </View>
      <DoublePointsMondayAlertBanner />
      {isAuthenticated && (
        <>
          <MqSwitch $ltDesktop={() => <InStoreCard />} />
          <View style={mqStyles.rewardsPointsProgressContainer}>
            <MqSwitch
              $gteDesktop={() => (
                <Image
                  contentFit="contain"
                  style={[
                    mqStyles.rewardsPointsProgressImage,
                    {
                      transform: [{ rotateY: '180deg' }],
                    },
                  ]}
                  source={require('./sub.png')}
                />
              )}
            />
            <RewardsPointsProgress
              style={mqStyles.rewardsPointsProgress}
              loading={isLoyaltyPageLoading}
            />
            <MqSwitch
              $gteDesktop={() => (
                <Image
                  contentFit="contain"
                  style={mqStyles.rewardsPointsProgressImage}
                  source={require('./sub.png')}
                />
              )}
            />
          </View>
          <TierProgressAlert style={mqStyles.tierProgressAlert} />
          {shouldShowExpiryDisclaimer && (
            <InlineAlert
              message={formatMessage(
                { id: 'rewardsPointsWillExpireDate' },
                {
                  date: (
                    <Text.Ui weight="semibold" size="md">
                      {loyaltyUserExpireDateFormattedText}
                    </Text.Ui>
                  ),
                }
              )}
              status="warning"
              style={mqStyles.disclaimerContainer}
            />
          )}
          {renderMaxRedemptionLimitAlert()}
        </>
      )}
    </View>
  );
}

const useMqStyles = createMqStyles({
  container: {
    $base: {
      width: '100%',
      paddingTop: 32,
    },
    $gteDesktop: {
      paddingTop: 64,
    },
  },
  content: {
    $base: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexDirection: 'row',
    },
  },
  separator: {
    $base: {
      height: 20,
      width: 1,
      marginHorizontal: 24,
      backgroundColor: tokens.colors.$blackOpacity10,
    },
  },
  pointsBalance: {
    $base: {
      flex: 1,
    },
    $ltDesktop: {
      marginTop: 4,
    },
  },
  disclaimerContainer: {
    $base: {
      marginTop: 24,
      borderRadius: 8,
    },
    $gteDesktop: {
      marginTop: 16,
    },
  },
  rewardsPointsProgressContainer: {
    $base: {
      marginTop: 24,
    },
    $gteDesktop: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: 16,
      borderWidth: 1,
      borderRadius: 8,
      borderColor: tokens.colors.$blackOpacity10,
      minHeight: 205,
    },
  },
  rewardsPointsProgressImage: {
    $gteDesktop: {
      height: '100%',
      aspectRatio: 0.72,
    },
  },
  rewardsPointsProgress: {
    $gteDesktop: {
      paddingVertical: 44,
      flex: 1,
      marginHorizontal: 32,
    },
  },
  tierProgressAlert: {
    $base: {
      marginTop: 24,
    },
    $gteDesktop: {
      marginTop: 16,
    },
  },
});
